import { get } from 'lodash'
import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'

import { OpportunityResponseWithRecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'
import { useErrorNotification, useSuccessNotification } from 'civic-champs-shared/api/hooks'
import { encodeOccurrenceFromEvent } from 'Event/helpers/encodeOccurrence'
import { useOpportunityPrivateTokens } from 'Event/scenes/events/detail/private-token/hooks'
import { OpportunityVisibility as OpportunityVisibilityType } from 'Event/interfaces'
import { useGetStableLink } from 'Event/hooks/useGetStableLink'

const useStyles = makeStyles({
  link: {
    color: '#0F5DB5',
  },
  copy: { marginLeft: '8px', cursor: 'pointer', color: '#0F5DB5', width: '20px', marginBottom: '-8px' },
})

// Based off the original link implementation in <EventVisibility/>
const OldEventLink = ({ event }: { event: OpportunityResponseWithRecurrenceInfo }) => {
  const encodedOccurrence = encodeOccurrenceFromEvent(event)
  const classes = useStyles()
  const { oneToken, loading } = useOpportunityPrivateTokens(encodedOccurrence)
  const onSuccess = useSuccessNotification(true)
  const isPrivate = event.visibility === OpportunityVisibilityType.PRIVATE && oneToken
  let root = process.env.REACT_APP_EVENTS_SITE_ENDPOINT
  let orgId = get(event, 'organization.id')
  let baseUrl = `${root}/organizations/${orgId}/opportunities/${encodedOccurrence}`

  if (isPrivate) {
    baseUrl += '?token=' + oneToken
  }
  return loading ? null : (
    <>
      <br />
      <a target="_blank" rel="noopener noreferrer" className={classes.link} href={baseUrl}>
        {isPrivate ? 'Private Registration Link' : 'Open Registration Link'}
      </a>
      <FileCopyOutlinedIcon
        className={classes.copy}
        onClick={async () => {
          await navigator.clipboard.writeText(baseUrl)
          onSuccess('Link copied')
        }}
      />
    </>
  )
}

function useBreaker(delay: number): [boolean, () => void] {
  const [value, setValue] = useState(false)

  const timeoutRef = useRef<number | null>(null)

  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  useEffect(() => {
    return clearTimeoutRef()
  }, [])

  const tripBreaker = useCallback(() => {
    clearTimeoutRef()
    setValue(true)
    timeoutRef.current = setTimeout(() => {
      setValue(false)
    }, delay)
  }, [delay])

  return [value, tripBreaker]
}

const NewEventLink = ({ event }: { event: OpportunityResponseWithRecurrenceInfo }) => {
  const classes = useStyles()

  const onSuccess = useSuccessNotification(true)
  const onError = useErrorNotification()

  const [getStableLink, { loading }] = useGetStableLink()
  const [breakerSwitch, tripBreaker] = useBreaker(500) // minimum load spinner time to prevent flickering

  const isPrivate = event.visibility === OpportunityVisibilityType.PRIVATE

  const getUrl = async (): Promise<string> => {
    tripBreaker()

    try {
      const encodedOccurrence = encodeOccurrenceFromEvent(event)
      const stableLink = await getStableLink(encodedOccurrence)

      const root = process.env.REACT_APP_EVENTS_SITE_ENDPOINT
      const orgId = get(event, 'organization.id')

      return `${root}/organizations/${orgId}/event_links/${stableLink.link}`
    } catch (error) {
      onError('Failed to get link', error)
      throw error
    }
  }

  const handleLinkClick = async (e: MouseEvent) => {
    e.preventDefault()
    if (loading) return

    const url = await getUrl()

    window.open(url, '_blank', 'noopener, noreferrer')
  }

  const handleCopyClick = async () => {
    const url = await getUrl()

    const copyToClipboard = async () => {
      await navigator.clipboard.writeText(url)
      onSuccess('Link copied')
    }

    if (document.hasFocus()) {
      copyToClipboard()
    } else {
      const timestamp = new Date()

      const onFocus = async () => {
        window.removeEventListener('focus', onFocus)

        // Confusing to copy to clipboard if a significant amount of time has passed (> 1min)
        if (new Date().getTime() - timestamp.getTime() < 60000) {
          return copyToClipboard()
        }
      }

      window.addEventListener('focus', onFocus)
    }
  }

  return (
    <>
      <br />
      <a className={classes.link} href="#" onClick={handleLinkClick}>
        {isPrivate ? 'Private Registration Link' : 'Open Registration Link'}
      </a>
      {loading || breakerSwitch ? (
        <CircularProgress className={classes.copy} size="1rem" />
      ) : (
        <FileCopyOutlinedIcon className={classes.copy} onClick={handleCopyClick} />
      )}
    </>
  )
}

export const EventStableLink = ({ event }: { event: OpportunityResponseWithRecurrenceInfo }) => {
  const isStableLinksEnabled = useFeatureEnabled('EventStableLinks')

  return isStableLinksEnabled ? <NewEventLink event={event} /> : <OldEventLink event={event} />
}
